import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { TimetableItem } from "../../domain/contracts/public-booking";

/** Time range for day/week views: 8:00 - 21:00 */
export const TIME_START_HOUR = 8;
export const TIME_END_HOUR = 21;
export const SLOT_MINUTES = 30;

/** Grid row = 15 min (2 ช่อง per 30-min row) - ใช้สำหรับ labels + span */
export const GRID_ROW_MINUTES = 15;

/** Extract time part from "HH:mm:ss", "HH:mm", or "YYYY-MM-DD HH:mm:ss" */
function extractTimePart(s: string): string {
  const trimmed = s.trim();
  const afterT = trimmed.includes("T") ? trimmed.split("T")[1] : trimmed;
  const afterSpace =
    (afterT ?? trimmed).split(/\s+/).pop() ?? afterT ?? trimmed;
  return String(afterSpace ?? "").split(".")[0] ?? "";
}

/** Convert "HH:mm:ss" or "HH:mm" to minutes since midnight */
export function timeToMinutes(timeStr: string): number {
  const timePart = extractTimePart(String(timeStr ?? ""));
  const parts = timePart.split(":");
  const h = Number.parseInt(parts[0] ?? "0", 10);
  const m = Number.parseInt(parts[1] ?? "0", 10);
  return (Number.isNaN(h) ? 0 : h) * 60 + (Number.isNaN(m) ? 0 : m);
}

/** Add minutes to "HH:mm" and return "HH:mm" */
export function addMinutesToTime(timeStr: string, minutes: number): string {
  const total = timeToMinutes(timeStr) + minutes;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Format time for display (HH:mm) - handles HH:mm and HH:mm:ss */
export function formatTimeDisplay(timeStr: string): string {
  const timePart = extractTimePart(String(timeStr ?? ""));
  const parts = timePart.split(":");
  const h = (parts[0] ?? "00").replace(/\D/g, "").padStart(2, "0") || "00";
  const m = (parts[1] ?? "00").replace(/\D/g, "").padStart(2, "0") || "00";
  return `${h}:${m}`;
}

/** Get list of time slot labels (15-min grid: 2 ช่อง per 30-min row) */
export function getTimeSlotLabels(): string[] {
  const labels: string[] = [];
  for (let h = TIME_START_HOUR; h <= TIME_END_HOUR; h++) {
    for (let m = 0; m < 60; m += GRID_ROW_MINUTES) {
      labels.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      );
    }
  }
  return labels;
}

/** Group 15-min slots into pairs for display: [[8:00,8:15], [8:30,8:45], ...] */
export function getTimeSlotGroups(): [string, string][] {
  const labels = getTimeSlotLabels();
  const groups: [string, string][] = [];
  for (let i = 0; i < labels.length; i += 2) {
    const a = labels[i];
    const b = labels[i + 1];
    if (a) {
      groups.push([a, b ?? addMinutesToTime(a, GRID_ROW_MINUTES)]);
    }
  }
  return groups;
}

/** Group items by date (YYYY-MM-DD) */
export function groupItemsByDate(
  items: TimetableItem[],
): Map<string, TimetableItem[]> {
  const map = new Map<string, TimetableItem[]>();
  for (const item of items) {
    const list = map.get(item.date) ?? [];
    list.push(item);
    map.set(item.date, list);
  }
  return map;
}

/** Group items by date then by roomId (each room's list sorted by startTime) */
export function groupItemsByDateAndRoom(
  items: TimetableItem[],
): Map<string, Map<string, TimetableItem[]>> {
  const byDate = groupItemsByDate(items);
  const result = new Map<string, Map<string, TimetableItem[]>>();
  for (const [date, list] of byDate) {
    const byRoom = new Map<string, TimetableItem[]>();
    for (const item of list) {
      const roomList = byRoom.get(item.roomId) ?? [];
      roomList.push(item);
      byRoom.set(item.roomId, roomList);
    }
    for (const roomList of byRoom.values()) {
      roomList.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    result.set(date, byRoom);
  }
  return result;
}

/** Count items per date (for month view) */
export function countByDate(items: TimetableItem[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(item.date, (map.get(item.date) ?? 0) + 1);
  }
  return map;
}

export type CountByType = {
  class: number;
  private: number;
  room_booking: number;
};

/** Count items per date by type (for month view badges) */
export function countByDateAndType(
  items: TimetableItem[],
): Map<string, CountByType> {
  const map = new Map<string, CountByType>();
  for (const item of items) {
    const curr = map.get(item.date) ?? {
      class: 0,
      private: 0,
      room_booking: 0,
    };
    curr[item.type] += 1;
    map.set(item.date, curr);
  }
  return map;
}

/** Get date range for week (start Monday for display, or Sunday - check spec: 7 columns Sun-Sat) */
export function getWeekRange(date: Date): { start: Date; end: Date } {
  const start = startOfWeek(date, { weekStartsOn: 0 });
  const end = endOfWeek(date, { weekStartsOn: 0 });
  return { start, end };
}

export function getMonthRange(date: Date): { start: Date; end: Date } {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  return { start, end };
}

export function toYMD(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/** Check if item overlaps with time slot (slotStart/slotEnd in HH:mm) */
export function itemOverlapsSlot(
  item: TimetableItem,
  date: string,
  slotStart: string,
  slotEnd: string,
): boolean {
  if (item.date !== date) return false;
  const itemStart = timeToMinutes(item.startTime);
  const itemEnd = timeToMinutes(item.endTime);
  const s = timeToMinutes(slotStart);
  const e = timeToMinutes(slotEnd);
  return itemStart < e && itemEnd > s;
}

/**
 * Number of grid rows (15-min each) an event spans (for rowSpan).
 * 1 row = 15 min; 2 rows = 1 ช่อง 30 min.
 * e.g. 9:00–10:30 → 6 rows; 12:00–13:00 → 4 rows; 12:15–13:15 → 4 rows.
 */
export function getEventSlotSpan(item: TimetableItem): number {
  const start = timeToMinutes(item.startTime);
  const end = timeToMinutes(item.endTime);
  const startRow = Math.floor(start / GRID_ROW_MINUTES);
  const endRow = Math.floor(end / GRID_ROW_MINUTES);
  return Math.max(1, endRow - startRow);
}

/** Check if this slot is the start slot of the event (slot contains event.startTime) */
export function slotIsEventStart(
  item: TimetableItem,
  slotStart: string,
  slotEnd: string,
): boolean {
  const itemStart = timeToMinutes(item.startTime);
  const s = timeToMinutes(slotStart);
  const e = timeToMinutes(slotEnd);
  return itemStart >= s && itemStart < e;
}

/** Left border accent class for event block (like image: blue class, grey private, dark grey booking) */
export function getEventBlockAccentClass(
  type: TimetableItem["type"],
  conflictIds?: Set<string>,
  item?: TimetableItem,
): string {
  const key = item ? `${item.type}-${item.id}` : "";
  const isConflict = item && conflictIds?.has(key);
  if (isConflict) return "border-l-4 border-l-destructive";
  switch (type) {
    case "class":
      return "border-l-4 border-l-primary";
    case "private":
      return "border-l-4 border-l-muted-foreground";
    case "room_booking":
      return "border-l-4 border-l-border";
    default:
      return "border-l-4 border-l-muted-foreground";
  }
}

/** Get CSS class for session type (subtle colors per spec) */
export function getSessionTypeClass(type: TimetableItem["type"]): string {
  switch (type) {
    case "class":
      return "bg-muted border-border";
    case "private":
      return "bg-muted/80 border-border";
    case "room_booking":
      return "bg-muted/60 border-border";
    default:
      return "bg-muted border-border";
  }
}

/** Get CSS class for event cell, optionally with conflict styling */
export function getEventCellClass(
  item: TimetableItem,
  conflictIds?: Set<string>,
): string {
  const base = getSessionTypeClass(item.type);
  const key = `${item.type}-${item.id}`;
  const isConflict = conflictIds?.has(key);
  const isCancelled = item.status === "cancelled";
  const isCompleted = item.status === "completed";
  const classes = [base];
  if (isCancelled) classes.push("opacity-70 line-through bg-destructive/20");
  else if (isCompleted) classes.push("opacity-80 text-muted-foreground");
  if (isConflict) classes.push("border-l-4 border-l-destructive");
  return classes.join(" ");
}
